import { SparklineMini, type SparklineTone } from './SparklineMini';
import type { StatusLevel } from './StatusPill';


export type BearingFrequency = {
  label: string; // "BPFI", "BPFO", "BSF", "FTF", "1×RPM", "2×RPM"
  hz: string; // formatted "176.4 Hz"
  series: number[];
  severity: StatusLevel;
  rising: boolean;
  /** Current amplitude at this frequency, formatted with unit. */
  amplitude?: string;
  /** Multiplier vs. baseline (e.g. "4.2×"). Omit if no baseline. */
  ratio?: string;
};

export type BearingFreqTrackerProps = {
  frequencies: BearingFrequency[];
  state?: 'default' | 'loading';
  /**
   * Click handler invoked when an operator selects a fault-frequency
   * row. Receives the numeric Hz parsed from `f.hz`. S5 wires this to
   * focus the SpectrumPanel on that frequency.
   */
  onSelect?: (hz: number, frequency: BearingFrequency) => void;
  /** Highlight a row as currently selected (e.g. last row clicked). */
  selectedLabel?: string;
};

const SEVERITY_TONE: Record<StatusLevel, SparklineTone> = {
  critical: 'critical',
  warn: 'warn',
  watch: 'watch',
  healthy: 'healthy',
  info: 'neutral',
};

const DOT: Record<StatusLevel, string> = {
  critical: 'bg-critical',
  warn: 'bg-warn',
  watch: 'bg-watch',
  healthy: 'bg-healthy',
  info: 'bg-info',
};

/**
 * Six stacked rows showing per-bearing-fault frequency trend. Rendered
 * as a table so screen readers can announce each row by its fault
 * frequency name; severity is double-encoded via the leading dot + the
 * sparkline stroke tone.
 */
export function BearingFreqTracker({
  frequencies,
  state = 'default',
  onSelect,
  selectedLabel,
}: BearingFreqTrackerProps) {
  if (state === 'loading') {
    return (
      <div className="w-full min-w-0 overflow-x-auto rounded-r-card border border-gray-300 bg-gray-50">
      <table
        aria-busy="true"
        aria-label="Bearing fault frequencies loading"
        className="w-full"
      >
        <tbody>
          {Array.from({ length: 6 }).map((_, i) => (
            <tr key={i} className="animate-pulse">
              <td className="p-sp-2"><span className="block h-3 w-16 rounded-r-chip bg-gray-100" /></td>
              <td className="p-sp-2"><span className="block h-3 w-12 rounded-r-chip bg-gray-100" /></td>
              <td className="p-sp-2"><span className="block h-4 w-24 rounded-r-chip bg-gray-100" /></td>
              <td className="p-sp-2"><span className="block h-3 w-12 rounded-r-chip bg-gray-100" /></td>
              <td className="p-sp-2"><span className="block h-3 w-10 rounded-r-chip bg-gray-100" /></td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 overflow-x-auto rounded-r-card border border-gray-300 bg-gray-50">
    <table
      aria-label="Bearing fault-frequency trends"
      className="w-full border-separate border-spacing-0 text-fs-meta"
    >
      <thead>
        <tr className="text-gray-700">
          <th scope="col" className="px-sp-2 py-sp-2 text-left font-medium uppercase tracking-[0.14em] text-fs-eyebrow">Fault</th>
          <th scope="col" className="px-sp-2 py-sp-2 text-left font-medium uppercase tracking-[0.14em] text-fs-eyebrow">Frequency</th>
          <th scope="col" className="px-sp-2 py-sp-2 text-left font-medium uppercase tracking-[0.14em] text-fs-eyebrow">Trend</th>
          <th scope="col" className="px-sp-2 py-sp-2 text-right font-medium uppercase tracking-[0.14em] text-fs-eyebrow">Amplitude</th>
          <th scope="col" className="px-sp-2 py-sp-2 text-right font-medium uppercase tracking-[0.14em] text-fs-eyebrow">×base</th>
        </tr>
      </thead>
      <tbody>
        {frequencies.map((f) => {
          const isSelected = selectedLabel === f.label;
          const clickable = !!onSelect;
          return (
            <tr
              key={f.label}
              data-selected={isSelected || undefined}
              onClick={
                clickable
                  ? () => {
                      const hz = parseFloat(f.hz);
                      if (Number.isFinite(hz)) onSelect!(hz, f);
                    }
                  : undefined
              }
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        const hz = parseFloat(f.hz);
                        if (Number.isFinite(hz)) onSelect!(hz, f);
                      }
                    }
                  : undefined
              }
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              aria-label={
                clickable
                  ? `Focus spectrum on ${f.label} at ${f.hz}`
                  : undefined
              }
              className={
                'border-t border-gray-300 ' +
                (clickable
                  ? 'cursor-pointer hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-info '
                  : '') +
                (isSelected ? 'bg-info-bg' : '')
              }
            >
              <th
                scope="row"
                className="border-t border-gray-300 px-sp-2 py-sp-2 text-left font-medium text-gray-900"
              >
                <span className="inline-flex items-center gap-sp-2">
                  <span
                    aria-hidden
                    className={`inline-block h-sp-2 w-sp-2 rounded-r-chip ${DOT[f.severity]}`}
                  />
                  {f.label}
                </span>
              </th>
              <td className="border-t border-gray-300 px-sp-2 py-sp-2 font-mono text-gray-700">
                {f.hz}
              </td>
              <td className="border-t border-gray-300 px-sp-2 py-sp-2">
                <SparklineMini series={f.series} tone={SEVERITY_TONE[f.severity]} width={56} />
              </td>
              <td className="border-t border-gray-300 px-sp-2 py-sp-2 text-right font-mono text-gray-900">
                {f.amplitude ?? '—'}
              </td>
              <td className="border-t border-gray-300 px-sp-2 py-sp-2 text-right font-mono text-gray-700">
                <span aria-label={f.rising ? 'rising' : 'stable'} title={f.rising ? 'rising' : 'stable'}>
                  {f.rising ? '↑ ' : '↓ '}
                </span>
                {f.ratio ?? '—'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}
